import {
  isSessionExpiredResult,
  type SessionExpiredResult,
} from "@/types/sessionExpiry";
import { notifySessionExpired } from "@/utils/common/logout";

/**
 * Raises the session-expired modal for a {@link SessionExpiredResult} and hands
 * the caller back the ordinary failure value it already renders.
 */
export function unwrapSessionExpiry<T>(
  value: T | SessionExpiredResult,
  fallback: T
): T {
  return handledSessionExpiry(value) ? fallback : (value as T);
}

/** `true` when the session is gone — and the modal has been raised by then. */
export function handledSessionExpiry(
  value: unknown
): value is SessionExpiredResult {
  if (!isSessionExpiredResult(value)) return false;
  notifySessionExpired();
  return true;
}

/**
 * Folds a rejection and a session-expired marker into the same `null`: to the
 * caller both are "couldn't read", and the modal has already been raised.
 */
export async function settled<T>(
  read: () => Promise<T | SessionExpiredResult>
): Promise<T | null> {
  try {
    return unwrapSessionExpiry<T | null>(await read(), null);
  } catch {
    return null;
  }
}
